// src/components/shared/forms/DynamicListInput.jsx
import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { FormField, TextInput, TextArea, SelectInput, Toggle } from './FormComponents';
import { ImageUpload } from './ImageUpload';

export const DynamicListInput = ({ 
  value = [], 
  onChange, 
  itemTemplate, 
  addLabel = 'Add Item',
  disabled = false,
  className = ""
}) => {
  const addItem = () => {
    const newItem = {};
    Object.keys(itemTemplate).forEach(key => {
      const field = itemTemplate[key];
      newItem[key] = field.type === 'toggle' ? false : '';
    });
    onChange([...value, newItem]);
  };

  const updateItem = (index, field, fieldValue) => {
    const updatedItems = value.map((item, i) => 
      i === index ? { ...item, [field]: fieldValue } : item
    );
    onChange(updatedItems);
  };

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {value.map((item, index) => (
        <div key={index} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <span className="text-sm font-medium text-gray-700">Item {index + 1}</span>
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="text-red-600 hover:text-red-800 disabled:opacity-50"
              disabled={disabled}
            >
              <Minus className="h-4 w-4" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(itemTemplate).map(([fieldKey, fieldConfig]) => (
              <FormField 
                key={fieldKey} 
                label={fieldConfig.label} 
                required={fieldConfig.required}
                className={fieldConfig.type === 'textarea' ? 'md:col-span-2' : ''}
              >
                {fieldConfig.type === 'textarea' ? (
                  <TextArea
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    rows={3}
                    disabled={disabled}
                  />
                ) : fieldConfig.type === 'select' ? (
                  <SelectInput
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    options={fieldConfig.options}
                    disabled={disabled}
                  />
                ) : fieldConfig.type === 'toggle' ? (
                  <Toggle
                    checked={item[fieldKey] || false}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    label={fieldConfig.label}
                    disabled={disabled}
                  />
                ) : fieldConfig.type === 'image' ? (
                  <ImageUpload
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    maxSize={2}
                    folder={fieldKey}
                    disabled={disabled}
                  />
                ) : (
                  <TextInput
                    value={item[fieldKey] || ''}
                    onChange={(val) => updateItem(index, fieldKey, val)}
                    placeholder={fieldConfig.placeholder}
                    type={fieldConfig.type || 'text'}
                    disabled={disabled}
                  />
                )}
              </FormField>
            ))}
          </div>
        </div>
      ))}
      
      <button
        type="button"
        onClick={addItem}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:text-gray-900 hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Plus className="h-4 w-4" />
        {addLabel}
      </button>
    </div>
  );
};